import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Gift, ArrowRight, AlertCircle } from 'lucide-react';

const PAYPAL_GIFTCARD_WEBHOOK = 'https://n8n.cogniiq.co/webhook/paypal-return-giftcard';

const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  delay: Math.random() * 2,
  size: Math.random() * 5 + 3,
  color: [
    '#C8311A',
    '#e05540',
    '#ff6b50',
    '#ffd2cb',
    '#1a1a1a',
    '#888',
  ][Math.floor(Math.random() * 6)],
  rotate: Math.random() * 360,
  shape: Math.random() > 0.5 ? 'circle' : 'square',
}));

export default function GutscheinPaymentSuccessPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const orderID = urlParams.get('token') || '';
  const email = urlParams.get('email') || '';
  const amount = urlParams.get('amount') || '';

  const [captureState, setCaptureState] = useState<'idle' | 'capturing' | 'done' | 'error'>(
    orderID ? 'capturing' : 'idle'
  );
  const [, setCaptureError] = useState('');

  useEffect(() => {
    document.title = 'Zahlung erfolgreich – Gutschein';
  }, []);

  useEffect(() => {
    if (!orderID) return;

    const key = `paypal_giftcard_processed_${orderID}`;
    if (sessionStorage.getItem(key)) {
      setCaptureState('done');
      return;
    }

    (async () => {
      try {
        const res = await fetch(PAYPAL_GIFTCARD_WEBHOOK, {
          method: 'POST',
          body: JSON.stringify({ orderID }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Webhook fehlgeschlagen (${res.status}): ${text}`);
        }

        sessionStorage.setItem(key, '1');
        setCaptureState('done');
      } catch (e) {
        console.error('[PayPal Giftcard Capture]', e);
        setCaptureError(e instanceof Error ? e.message : 'Unbekannter Fehler');
        setCaptureState('error');
      }
    })();
  }, [orderID]);

  if (captureState === 'capturing') {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex flex-col items-center justify-center py-6 gap-5 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--svh-crest-wash)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full animate-spin"
                    style={{ border: '2px solid var(--svh-crest-wash)', borderTopColor: 'var(--svh-crest)' }}
                  />
                </div>
                <div>
                  <p className="svh-h3 mb-1.5">Gutschein wird erstellt …</p>
                  <p className="svh-meta">Einen Moment bitte. Bitte schließen Sie dieses Fenster nicht.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  if (captureState === 'error') {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" strokeWidth={1.75} />
                </div>
              </div>
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-red-700 mb-3 text-center">
                Gutschein fehlgeschlagen
              </p>
              <h1 className="svh-h2 text-center mb-4">Gutschein konnte nicht abgeschlossen werden</h1>
              <p className="svh-body text-center mb-6">Bitte kontaktieren Sie den Verein.</p>

              <hr className="svh-rule mb-6" />

              <a href="/" className="svh-btn svh-btn--primary svh-focus w-full">
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16 overflow-hidden relative"
    >
      {/* Restrained confetti (existing celebratory motion, softened) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        {CONFETTI.map((p) => (
          <motion.div
            key={p.id}
            className="absolute pointer-events-none"
            style={{
              left: `${p.x}%`,
              bottom: '-12px',
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              borderRadius: p.shape === 'circle' ? '50%' : '2px',
              rotate: p.rotate,
            }}
            animate={{
              y: [0, -(window.innerHeight + 120)],
              opacity: [0, 0.7, 0],
              rotate: [p.rotate, p.rotate + 360],
            }}
            transition={{
              duration: 5 + Math.random() * 4,
              delay: p.delay,
              repeat: Infinity,
              repeatDelay: Math.random() * 6 + 2,
              ease: 'easeOut',
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-[560px]"
      >
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="svh-card svh-card-lg svh-card-raised overflow-hidden"
        >
          <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
          <div className="p-8 md:p-10">

            {/* Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <Gift className="w-8 h-8 text-emerald-600" strokeWidth={1.75} />
              </div>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
              className="text-center mb-8"
            >
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
                Zahlung bestätigt
              </p>
              <h1 className="svh-h2 mb-4">Vielen Dank für Ihren Kauf</h1>
              <p className="svh-body">
                Ihre Zahlung war erfolgreich. Der Gutscheincode wird in Kürze per E-Mail zugestellt.
              </p>
            </motion.div>

            {/* Amount */}
            {amount && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="rounded-[10px] px-5 py-4 mb-4"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <div className="flex items-center gap-3">
                  <Gift className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                  <div>
                    <p className="svh-label mb-0.5">Gutscheinwert</p>
                    <p className="text-[22px] font-bold svh-num" style={{ color: 'var(--svh-ink)' }}>{amount} €</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* E-mail row */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              className="rounded-[10px] overflow-hidden mb-6"
              style={{ border: '1px solid var(--svh-border)' }}
            >
              <div className="px-5 py-4" style={{ background: 'var(--svh-surface-sub)' }}>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="svh-label mb-1">Code wird per E-Mail versandt</p>
                    <p className="text-[14px] leading-relaxed break-words" style={{ color: 'var(--svh-ink-2)' }}>
                      {email ? (
                        <>
                          Wir schicken Ihren Gutscheincode an{' '}
                          <span className="font-semibold" style={{ color: 'var(--svh-ink)' }}>
                            {email}
                          </span>
                          .
                        </>
                      ) : (
                        'Wir schicken Ihren Gutscheincode an die bei der Bestellung angegebene E-Mail-Adresse.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 px-5 py-4 bg-amber-50/70 border-t" style={{ borderColor: 'var(--svh-border)' }}>
                <span className="text-[15px] flex-shrink-0" role="img" aria-label="Spam">📬</span>
                <p className="text-[13px] leading-relaxed text-amber-900">
                  <span className="font-semibold">Keine E-Mail erhalten?</span> Bitte auch den{' '}
                  <span className="font-semibold">Spam- oder Junk-Ordner</span> prüfen — manchmal landet unsere Mail dort.
                </p>
              </div>
            </motion.div>

            {/* Steps */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="mb-6"
            >
              <p className="svh-label mb-4">So geht&apos;s weiter</p>
              <div className="space-y-3">
                {[
                  'Sie erhalten Ihren Gutscheincode per E-Mail',
                  'Code beim Buchen einfach eingeben und einlösen',
                  'Court buchen und losspielen',
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-white"
                      style={{ background: 'var(--svh-crest)' }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-[14px] leading-snug" style={{ color: 'var(--svh-ink-2)' }}>{step}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <hr className="svh-rule mb-6" />

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.45 }}
            >
              <button
                onClick={() => (window.location.href = '/')}
                className="svh-btn svh-btn--primary svh-focus w-full"
              >
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Support */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="text-center mt-6"
        >
          <p className="svh-meta">Fragen? Schreiben Sie uns:</p>
          <a
            href="mailto:info@svhpadeltennis.de"
            className="svh-focus inline-flex items-center justify-center min-h-[44px] px-4 text-[13px] font-semibold underline underline-offset-4"
            style={{ color: 'var(--svh-crest)' }}
          >
            info@svhpadeltennis.de
          </a>
        </motion.div>

        <p className="svh-meta text-center mt-3">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
